/**
 * The in-context file chip ("Refactor target", `multiAgentIdeStore.pendingRefactorFile`) as a lane-v2 turn input.
 *
 * The old lane folded the chip into `user_intent`; a lane-v2 turn has no such field, so the chip rides as a
 * pre-resolved `code` attachment plus `ide_state.open_file`. Pure so the caps are unit-testable.
 */
import type { AttachmentIn, IdeStateIn } from "./types";

/** MSA caps (`TurnIn`): attachments per turn and characters per attachment. */
export const MAX_TURN_ATTACHMENTS = 10;
export const MAX_ATTACHMENT_CHARS = 200_000;

/** MSA bounds: attachment name, `ide_state.open_file`, and the folded total (each attachment counts at most MAX_FOLDED_PER_ATTACHMENT). */
export const MAX_ATTACHMENT_NAME_CHARS = 200;
export const MAX_OPEN_FILE_CHARS = 300;
export const MAX_TOTAL_CONTEXT_CHARS = 120_000;
export const MAX_FOLDED_PER_ATTACHMENT = 25_000;

export const TRUNCATION_MARKER = "\n\n[... truncated: this file is longer than the 200,000 character limit for one attachment ...]";

export interface ContextFile {
  id: string;
  name: string;
  content: string;
  metadataType?: string;
}

/** Clamp text to the per-attachment cap, ending in a visible marker when it was cut. Never exceeds the cap. */
export function clampAttachmentText(text: string, max = MAX_ATTACHMENT_CHARS): string {
  if (text.length <= max) return text;
  return text.slice(0, Math.max(0, max - TRUNCATION_MARKER.length)) + TRUNCATION_MARKER;
}

function languageFor(file: ContextFile): string {
  const type = file.metadataType ?? "";
  if (type === "ApexClass" || type === "ApexTrigger") return "apex";
  const ext = file.name.includes(".") ? file.name.split(".").pop()!.toLowerCase() : "";
  const byExt: Record<string, string> = {
    cls: "apex", trigger: "apex", js: "javascript", ts: "typescript", html: "html", css: "css", xml: "xml", json: "json", soql: "sql",
  };
  return byExt[ext] ?? (type ? "apex" : "text");
}

/** Markdown-fenced source. The fence is longer than any backtick run inside, so the content cannot close it early. */
function fenced(language: string, source: string): string {
  const longest = (source.match(/`+/g) ?? []).reduce((n, run) => Math.max(n, run.length), 0);
  const fence = "`".repeat(Math.max(3, longest + 1));
  return `${fence}${language}\n${source}\n${fence}`;
}

export function contextFileAttachment(rawFile: ContextFile): AttachmentIn {
  const file = { ...rawFile, name: rawFile.name.slice(0, MAX_ATTACHMENT_NAME_CHARS) };
  const language = languageFor(file);
  if (!file.content?.trim()) {
    // Source not loaded in the editor: a reference the agent can resolve from the org by name.
    return { kind: "code", name: file.name, language, markdown: `Context file: ${file.name}${file.metadataType ? ` (${file.metadataType})` : ""}. Its source was not loaded in the editor; read it from the org.` };
  }
  const overhead = fenced(language, "").length + 2; // fences + newlines, cheap upper bound for the cap
  const source = clampAttachmentText(file.content, MAX_ATTACHMENT_CHARS - overhead - 16);
  return { kind: "code", name: file.name, language, markdown: clampAttachmentText(fenced(language, source)) };
}

function foldedChars(a: AttachmentIn): number {
  return Math.min((a.markdown ?? a.content ?? "").length, MAX_FOLDED_PER_ATTACHMENT);
}

/**
 * The turn's attachments: the context file first (it is what the user pointed at), then the paperclip ones. A paperclip
 * that is the same source as the chip (same name and text) is dropped as a duplicate. Nothing is ever silently dropped
 * otherwise: more than the MSA cap, or a folded total over MSA's, REFUSES the send (`refusal`, in words) and sends nothing,
 * so what is on screen is what would have been sent. The caller clears only inputs that actually rode.
 */
export function composeTurnAttachments(
  context: AttachmentIn | null,
  paperclips: readonly AttachmentIn[],
): { attachments: AttachmentIn[]; rodeContext: boolean; rodePaperclips: boolean; refusal: string | null } {
  const distinct = context
    ? paperclips.filter((p) => !(p.name === context.name && (p.markdown ?? p.content ?? "").trim() !== "" && (context.markdown ?? "").includes((p.markdown ?? p.content ?? "").trim())))
    : [...paperclips];
  const all = context ? [context, ...distinct] : distinct;
  if (all.length > MAX_TURN_ATTACHMENTS) {
    return {
      attachments: [],
      rodeContext: false,
      rodePaperclips: false,
      refusal: `A message can carry at most ${MAX_TURN_ATTACHMENTS} attachments${context ? " including the open file" : ""}; you have ${all.length}. Remove ${all.length - MAX_TURN_ATTACHMENTS} and send again.`,
    };
  }
  const folded = all.reduce((n, a) => n + foldedChars(a), 0);
  if (folded > MAX_TOTAL_CONTEXT_CHARS) {
    return {
      attachments: [],
      rodeContext: false,
      rodePaperclips: false,
      refusal: "The attachments together are too large for one message. Remove some or send them in separate messages.",
    };
  }
  return { attachments: all, rodeContext: !!context, rodePaperclips: paperclips.length > 0, refusal: null };
}

/** `ide_state` with `open_file` set to the context file's name; the repo/branch hint stays when there is one. */
export function ideStateWithOpenFile(base: IdeStateIn | null, file: ContextFile | null): IdeStateIn | null {
  if (!file) return base;
  return { ...(base ?? {}), open_file: file.name.slice(0, MAX_OPEN_FILE_CHARS) };
}

/**
 * Everything one lane-v2 send puts on the wire besides the text, from the capabilities the server advertised.
 * A server without `turn_attachments` cannot take the chip or paperclips (they stay on screen); one without
 * `turn_ide_state` refuses `ide_state` (422).
 */
export function turnInputsFor(
  caps: { turnAttachments: boolean; turnIdeState: boolean },
  chip: ContextFile | null,
  paperclips: readonly AttachmentIn[],
  repoIdeState: IdeStateIn | null,
): { attachments: AttachmentIn[]; ideState: IdeStateIn | null; rodeContext: boolean; rodePaperclips: boolean; refusal: string | null } {
  const composed = caps.turnAttachments
    ? composeTurnAttachments(chip ? contextFileAttachment(chip) : null, paperclips)
    : { attachments: [] as AttachmentIn[], rodeContext: false, rodePaperclips: false, refusal: null };
  const ideState = caps.turnIdeState ? ideStateWithOpenFile(repoIdeState, chip) : null;
  return { ...composed, ideState };
}
